import React, { useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FormattedMessage, useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button, Title } from '@xsolla/ui-core';
import { Copy, Pencil } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { getOAuthSettings } from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { OauthConnectResponse, OauthSettings } from '../../../api/configurations/oauth2/types';
import { Error } from '../../../api/setup/getApiError';
import { PROJECT_LINKS } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { useChecklist } from '../../../contexts/settings';
import { HorizontalCenterColumnBlock } from '../../../styled';
import { getOAuthType } from '../../../utils';
import { HeaderTitle, LinkStylesUnderline, Table, ToNavigationError } from '../../ui';
import { ConnectOauthModal } from '../ConnectOauthModal';
import { RemoveOauthModal } from '../ConnectOauthModal/RemoveOauthModal';
import { SuccessConnectModal } from '../ConnectOauthModal/SuccessConnectModal';
import { UpdateOauthModal } from '../ConnectOauthModal/UpdateOauthModal';
import {
  AddOAuthButtonWrap,
  ClientId,
  ClientName,
  ClientSecret,
  EmptyTableWrapper,
  TableBlock,
  UpdateOauth
} from './style';

type Props = RouteComponentProps & {
  projectId: string;
};

const Oauth2Settings = ({ projectId, history }: Props): JSX.Element => {
  const intl = useIntl();
  const { OAUTH2 } = PROJECT_LINKS;
  const { merchantId } = useConfig();
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [loadDataError, setLoadDataError] = useState<Error | null>(null);
  const [baseNamesNumber, setBaseNamesNumber] = useState<number>(0);
  const [settingsOrderNumber, setSettingsOrderNumber] = useState<number>(0);
  const [OAuthMainData, setOAuthMainData] = useState<OauthConnectResponse>({
    id: 0,
    secret: ''
  });
  const { enableChecklistItem } = useChecklist(projectId);

  const [oauthSettings, setOauthSettings] = useState<OauthSettings[]>([]);

  const [onOpenRemoveModal, onCloseRemoveModal] = useModal(
    () => (
      <RemoveOauthModal
        setOauthSettings={setOauthSettings}
        id={oauthSettings[settingsOrderNumber]?.id as number}
        name={oauthSettings[settingsOrderNumber]?.name}
        onClose={onCloseRemoveModal}
      />
    ),
    [oauthSettings, settingsOrderNumber]
  );

  const [onOpenUpdateModal, onCloseUpdateModal] = useModal(
    () => (
      <UpdateOauthModal
        setOauthSettings={setOauthSettings}
        openRemoveModal={onOpenRemoveModal}
        merchantId={merchantId.toString()}
        oauthSettings={oauthSettings[settingsOrderNumber]}
        onClose={onCloseUpdateModal}
      />
    ),
    [settingsOrderNumber, oauthSettings]
  );

  const [onOpenSuccessModal, onCloseSuccessModal] = useModal(
    () => <SuccessConnectModal OAuthMainData={OAuthMainData} onClose={onCloseSuccessModal} />,
    [OAuthMainData]
  );

  const [openCreateModal, closeCreateModal] = useModal(
    () => (
      <ConnectOauthModal
        baseNamesNumber={baseNamesNumber}
        setOauthSettings={setOauthSettings}
        setOAuthMainData={setOAuthMainData}
        onOpenSuccessModal={onOpenSuccessModal}
        projectId={projectId}
        merchantId={merchantId.toString()}
        onClose={closeCreateModal}
      />
    ),
    [baseNamesNumber]
  );

  useEffect(() => {
    getOAuthSettings(projectId)
      .then((settings) => {
        setOauthSettings(settings.clients);
      })
      .catch((err) => setLoadDataError(err))
      .finally(() => setFetchingData(false));
  }, []);

  useEffect(() => {
    if (oauthSettings) {
      setBaseNamesNumber(
        oauthSettings.filter((client) =>
          client.name?.includes(
            intl.messages['authorization.settings.oauth2.server.name.placeholder'] as string
          )
        ).length
      );
    }
    if (oauthSettings?.length) {
      enableChecklistItem(ChecklistItems.SET_UP_OAUTH);
    }
  }, [oauthSettings]);

  const tableHeadProps = [
    { width: 5, separator: false },
    { width: 25, label: intl.messages['authorization.settings.oauth2.table.client-name'] },
    { width: 20, label: intl.messages['authorization.settings.oauth2.table.client-id'] },
    { width: 20, label: intl.messages['authorization.settings.oauth2.table.client-secret'] },
    { width: 22, label: intl.messages['authorization.settings.oauth2.table.auth-type'] },
    { width: 8, separator: false }
  ];

  const rowComponents = (
    { name, id, secret, grant_types, is_public, access }: OauthSettings,
    index: number
  ) => ({
    main: [
      <div>{}</div>,
      <ClientName>{name}</ClientName>,
      <ClientId>{id}</ClientId>,
      <CopyToClipboard text={secret}>
        <ClientSecret data-test-id={`oauth2-client-copy-key-${id}`}>
          {
            intl.messages[
              `authorization.settings.oauth2.table.${access.length ? 'server' : 'client'}-key`
            ]
          }
          <Copy />
        </ClientSecret>
      </CopyToClipboard>,
      <div>{getOAuthType(grant_types, is_public)}</div>,
      <UpdateOauth
        data-test-id="oauth2-client-edit"
        onClick={() => {
          onOpenUpdateModal();
          setSettingsOrderNumber(index);
        }}>
        <Pencil />
      </UpdateOauth>
    ]
  });

  const noDataContent = useMemo(() => {
    return (
      <EmptyTableWrapper>
        <Title>{intl.messages['authorization.settings.oauth2.table.empty.title']}</Title>
        <p>{intl.messages['authorization.settings.oauth2.table.empty.text']}</p>
      </EmptyTableWrapper>
    );
  }, []);

  if (loadDataError) {
    return <ToNavigationError error={loadDataError} pageName="oauth2-settings_open" />;
  }

  return (
    <>
      <HorizontalCenterColumnBlock>
        <HeaderTitle
          title={intl.formatMessage({ id: 'authorization.settings.oauth2.client.title' })}
          subtitle={intl.formatMessage({ id: 'authorization.settings.oauth2.client.subtitle' })}
          notification={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <FormattedMessage
              id="authorization.settings.oauth2.notification"
              values={{
                br: () => <br />,
                b: (chunk: string) => <b>{chunk}</b>,
                a: (chunk: string) => (
                  <LinkStylesUnderline>
                    <a
                      href={OAUTH2}
                      target="_blank"
                      rel="noreferrer"
                      data-test-id="oauth2-doc-link">
                      {chunk}
                    </a>
                  </LinkStylesUnderline>
                )
              }}
            />
          }
        />
      </HorizontalCenterColumnBlock>
      <TableBlock>
        <Table
          head={tableHeadProps}
          renderRow={rowComponents}
          tableData={oauthSettings}
          noDataContent={noDataContent}
          fetching={fetchingData}
        />
        <AddOAuthButtonWrap>
          <Button
            look="tertiary"
            palette="muted"
            size="md"
            onClick={openCreateModal}
            data-test-id="oauth2-add-client">
            {intl.messages['authorization.settings.oauth2.add']}
          </Button>
        </AddOAuthButtonWrap>
      </TableBlock>
    </>
  );
};

export default withRouter(Oauth2Settings);
